import { useState } from "react";
import { EquipmentSelect } from "@/components/ui/equipment-select";
import { format } from "date-fns";
import { Truck, ChevronDown, CalendarIcon, Loader2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { LocationAutocomplete } from "@/components/ui/location-autocomplete";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

export interface TruckFormData {
  origin: string;
  destination: string;
  openToAnywhere: boolean;
  availabilityDate: Date | undefined;
  equipmentType: string;
  length: string;
  maxWeight: string;
  loadType: "FTL" | "LTL" | "";
  notes: string;
  visible: boolean;
}

const INITIAL_DATA: TruckFormData = {
  origin: "",
  destination: "",
  openToAnywhere: false,
  availabilityDate: new Date(),
  equipmentType: "",
  length: "",
  maxWeight: "",
  loadType: "FTL",
  notes: "",
  visible: true,
};

const EMPTY_DATA: TruckFormData = {
  origin: "", destination: "", openToAnywhere: false, availabilityDate: undefined,
  equipmentType: "", length: "", maxWeight: "", loadType: "FTL",
  notes: "", visible: true,
};

/** @deprecated Use getLastTruckStorageKey(userId) instead */
export const LS_KEY = "lr-last-truck-post";

export const getLastTruckStorageKey = (userId: string) => `lr-last-truck-post-${userId}`;

export interface ContactInfo {
  name: string;
  companyName: string;
  phone: string;
  email: string;
}

/** The normalized payload that was actually inserted into truck_postings */
export interface PostedTruckData {
  origin: string;
  destination: string;
  openToAnywhere: boolean;
  equipmentType: string;
  loadType: string;
  lengthFt: number;
  maxWeightLbs: number;
}

interface Props {
  form: TruckFormData;
  setForm: React.Dispatch<React.SetStateAction<TruckFormData>>;
  contactInfo: ContactInfo;
  onSaveTemplate?: () => void;
  onSaveLastTruck?: () => void;
  onPostSuccess?: (data: PostedTruckData) => void;
  editTruckId?: string | null;
  onEditSuccess?: () => void;
}

type FieldErrors = Partial<Record<keyof TruckFormData, string>>;

const QUICK_EQUIPMENT = [
  { label: "Van", enumValue: "van" },
  { label: "Reefer", enumValue: "reefer" },
  { label: "Flatbed", enumValue: "flatbed" },
  { label: "Stepdeck", enumValue: "step_deck" },
  { label: "Conestoga", enumValue: "conestoga" },
] as const;

const MAX_LENGTH_FT = 99;
const MAX_WEIGHT_LBS = 99999;
const MAX_NOTES_LENGTH = 500;

const QUICK_NOTES = [
  { label: "Team driver", value: "Team driver" },
  { label: "Hazmat certified", value: "Hazmat certified" },
  { label: "Drop & hook", value: "Drop & hook" },
  { label: "TARP", value: "TARP" },
  { label: "Flexible dates", value: "Flexible dates" },
  { label: "No touch freight", value: "No touch freight" },
  { label: "Oversize", value: "Oversize" },
  { label: "Liftgate", value: "Liftgate" },
];

const ERROR_MESSAGES: Record<string, string> = {
  origin: "Please add an origin location so brokers know where your truck starts.",
  destination: "Please add a destination so brokers can match freight.",
  availabilityDate: "Please select when your truck is available.",
  equipmentType: "Please choose an equipment type.",
  loadType: "Please select FTL or LTL.",
};

export default function PostTruckFormCard({ form, setForm, contactInfo, onSaveTemplate, onSaveLastTruck, onPostSuccess, editTruckId, onEditSuccess }: Props) {
  const [errors, setErrors] = useState<FieldErrors>({});
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();

  const set = <K extends keyof TruckFormData>(key: K, val: TruckFormData[K]) => {
    setForm((prev) => ({ ...prev, [key]: val }));
    if (errors[key]) setErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });
  };

  const setNumericField = (key: "length" | "maxWeight", val: string, max: number) => {
    if (val === "") { set(key, ""); return; }
    const num = parseInt(val, 10);
    if (isNaN(num) || num < 0) return;
    if (num > max) { set(key, String(max)); return; }
    set(key, String(num));
  };

  const validate = (): boolean => {
    const e: FieldErrors = {};
    if (!form.origin.trim()) e.origin = ERROR_MESSAGES.origin;
    if (!form.destination.trim() && !form.openToAnywhere) e.destination = ERROR_MESSAGES.destination;
    if (!form.availabilityDate) e.availabilityDate = ERROR_MESSAGES.availabilityDate;
    if (!form.equipmentType) e.equipmentType = ERROR_MESSAGES.equipmentType;
    if (!form.loadType) e.loadType = ERROR_MESSAGES.loadType;
    if (form.length && (parseInt(form.length) <= 0)) e.length = "Length must be at least 1 ft.";
    if (form.maxWeight && (parseInt(form.maxWeight) <= 0)) e.maxWeight = "Weight must be at least 1 lb.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const saveToLocalStorage = () => {
    onSaveLastTruck?.();
  };

  const handleSubmit = async () => {
    if (!validate()) {
      toast({ title: "Please fix the errors above before posting.", variant: "destructive" });
      return;
    }

    if (!user) {
      toast({ title: "You must be logged in to post a truck.", variant: "destructive" });
      return;
    }

    setSubmitting(true);
    try {
      const { data: companyUser } = await supabase
        .from("company_users")
        .select("company_id")
        .eq("user_id", user.id)
        .eq("is_active", true)
        .maybeSingle();

      const availDate = form.availabilityDate!;
      const dateStr = format(availDate, "yyyy-MM-dd");

      // Build contact_info from profile-derived contactInfo prop
      const contactParts: string[] = [];
      if (contactInfo.name.trim()) contactParts.push(contactInfo.name.trim());
      if (contactInfo.phone.trim()) contactParts.push(contactInfo.phone.trim());
      if (contactInfo.email.trim()) contactParts.push(contactInfo.email.trim());
      if (contactInfo.companyName.trim()) contactParts.push(contactInfo.companyName.trim());

      const payload = {
        available_from: dateStr,
        available_to: dateStr,
        origin_location: form.origin.trim(),
        destination_location: form.openToAnywhere ? "Anywhere" : form.destination.trim(),
        equipment_type: form.equipmentType,
        load_type: form.loadType,
        length_ft: form.length ? parseInt(form.length) : 0,
        weight_capacity_lbs: form.maxWeight ? parseInt(form.maxWeight) : 0,
        contact_info: contactParts.join(" | "),
        comments: form.notes.trim() || null,
        status: form.visible ? "available" : "private",
      };

      if (editTruckId) {
        const { error } = await supabase.from("truck_postings").update(payload).eq("id", editTruckId);
        if (error) throw error;
      } else {
        const { error } = await supabase.from("truck_postings").insert({
          created_by: user.id,
          company_id: companyUser?.company_id || null,
          ...payload,
        });
        if (error) throw error;
      }

      // Capture the final normalized data before resetting
      const postedData: PostedTruckData = {
        origin: form.origin.trim(),
        destination: form.openToAnywhere ? "Anywhere" : form.destination.trim(),
        openToAnywhere: form.openToAnywhere,
        equipmentType: form.equipmentType,
        loadType: form.loadType,
        lengthFt: form.length ? parseInt(form.length) : 0,
        maxWeightLbs: form.maxWeight ? parseInt(form.maxWeight) : 0,
      };

      if (editTruckId) {
        toast({ title: "Truck updated successfully!" });
        onEditSuccess?.();
      } else {
        saveToLocalStorage();
        toast({ title: "Truck posted successfully!", description: "Brokers can now find your truck." });
        onPostSuccess?.(postedData);
        setForm({ ...EMPTY_DATA, availabilityDate: new Date() });
        setErrors({});
      }
      setErrors({});
    } catch (err: any) {
      console.error("[PostTruck] Error:", err);
      toast({ title: "Failed to post truck", description: err.message || "Please try again.", variant: "destructive" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDraft = () => {
    toast({ title: "Draft saved (mock)." });
  };

  const handleClear = () => {
    setForm({ ...EMPTY_DATA });
    setErrors({});
  };

  const fieldError = (key: keyof TruckFormData) =>
    errors[key] ? (
      <p className="text-[11px] mt-1" style={{ color: "hsl(var(--danger))" }}>{errors[key]}</p>
    ) : null;

  const errorBorder = (key: keyof TruckFormData) =>
    errors[key] ? "border-danger" : "";

  return (
    <div
      className="rounded-xl border border-border bg-card flex flex-col w-full min-w-0"
      style={{ boxShadow: "var(--shadow-card)" }}
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-border">
        <div className="flex items-center gap-2">
          <Truck className="h-3.5 w-3.5" style={{ color: "hsl(var(--brand-teal-deep))" }} />
          <h4 className="text-xs font-semibold uppercase tracking-wider text-foreground">Post Truck</h4>
        </div>
        <div className="flex items-center gap-1.5 text-[10px]">
          <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: "hsl(var(--success))" }} />
          <span className="font-medium text-muted-foreground">Draft</span>
          <ChevronDown className="h-3 w-3 text-muted-foreground" />
        </div>
      </div>

      <p className="px-4 pt-3 text-[11px] text-muted-foreground leading-relaxed">
        Share your available capacity so brokers can match freight in seconds.
      </p>

      <div className="px-4 pb-4 pt-2 space-y-4">
        <SectionHeader title="Truck & Route Details" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <Field label="Origin" required>
              <LocationAutocomplete
                value={form.origin}
                onChange={(val) => set("origin", val)}
                placeholder="City, ST or ZIP code"
                className={cn("h-9 text-xs", errorBorder("origin"))}
                error={errors.origin}
              />
              {fieldError("origin")}
            </Field>

            <Field label="Destination" required={!form.openToAnywhere}>
              {form.openToAnywhere ? (
                <Input
                  className="h-9 text-xs"
                  value="Open to anywhere"
                  disabled
                />
              ) : (
                <LocationAutocomplete
                  value={form.destination}
                  onChange={(val) => set("destination", val)}
                  placeholder="City, ST or ZIP code"
                  className={cn("h-9 text-xs", errorBorder("destination"))}
                  error={errors.destination}
                />
              )}
              {fieldError("destination")}
              <div className="flex items-center gap-2 pt-1">
                <Switch
                  checked={form.openToAnywhere}
                  onCheckedChange={(v) => set("openToAnywhere", v)}
                  className="scale-75 origin-left"
                />
                <span className="text-[10px] text-muted-foreground font-medium">Open to anywhere</span>
              </div>
            </Field>

            <Field label="Availability date" required>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal h-9 text-xs",
                      !form.availabilityDate && "text-muted-foreground",
                      errorBorder("availabilityDate"),
                    )}
                  >
                    <CalendarIcon className="mr-2 h-3.5 w-3.5" />
                    {form.availabilityDate ? format(form.availabilityDate, "PPP") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={form.availabilityDate}
                    onSelect={(d) => set("availabilityDate", d)}
                    disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
                    initialFocus
                    className="p-3 pointer-events-auto"
                  />
                </PopoverContent>
              </Popover>
              {fieldError("availabilityDate")}
            </Field>
          </div>

          <div className="space-y-3">
            <Field label="Equipment type" required>
              <EquipmentSelect
                value={form.equipmentType}
                onChange={(v) => set("equipmentType", v as string)}
                mode="single"
                placeholder="Select type"
                className={cn("h-9 text-xs", errorBorder("equipmentType"))}
                error={!!errors.equipmentType}
              />
              {/* Equipment Quick Buttons */}
              <div className="flex flex-wrap gap-1.5 mt-2">
                {QUICK_EQUIPMENT.map((eq) => (
                  <button
                    key={eq.enumValue}
                    type="button"
                    onClick={() => set("equipmentType", eq.enumValue)}
                    className={cn(
                      "rounded-md px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider border transition-colors",
                      form.equipmentType === eq.enumValue
                        ? "border-brand-teal/30 text-brand-teal-deep"
                        : "border-border text-muted-foreground hover:border-muted-foreground/40",
                    )}
                    style={
                      form.equipmentType === eq.enumValue
                        ? { backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))", borderColor: "hsl(170 85% 32% / 0.3)" }
                        : undefined
                    }
                  >
                    {eq.label}
                  </button>
                ))}
              </div>
              {fieldError("equipmentType")}
            </Field>

            <Field label={`Length (max ${MAX_LENGTH_FT} ft)`}>
              <div className="relative">
                <Input
                  className={cn("h-9 text-xs pr-10", errorBorder("length"))}
                  type="number"
                  placeholder="53"
                  min={0}
                  max={MAX_LENGTH_FT}
                  value={form.length}
                  onChange={(e) => setNumericField("length", e.target.value, MAX_LENGTH_FT)}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">ft</span>
              </div>
              {fieldError("length")}
            </Field>

            <Field label={`Max weight (max ${MAX_WEIGHT_LBS.toLocaleString()} lbs)`}>
              <div className="relative">
                <Input
                  className={cn("h-9 text-xs pr-12", errorBorder("maxWeight"))}
                  type="number"
                  placeholder="45000"
                  min={0}
                  max={MAX_WEIGHT_LBS}
                  value={form.maxWeight}
                  onChange={(e) => setNumericField("maxWeight", e.target.value, MAX_WEIGHT_LBS)}
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[10px] font-medium text-muted-foreground">lbs</span>
              </div>
              {fieldError("maxWeight")}
            </Field>

            <Field label="Load type" required>
              <div className="flex gap-2">
                {(["FTL", "LTL"] as const).map((t) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => set("loadType", t)}
                    className={cn(
                      "flex-1 rounded-md py-1.5 text-[11px] font-bold uppercase tracking-wider border transition-colors",
                      form.loadType === t
                        ? "border-brand-teal/30 text-brand-teal-deep"
                        : "border-border text-muted-foreground hover:border-muted-foreground/40",
                    )}
                    style={
                      form.loadType === t
                        ? { backgroundColor: "hsl(170 85% 32% / 0.12)", color: "hsl(var(--brand-teal-deep))", borderColor: "hsl(170 85% 32% / 0.3)" }
                        : undefined
                    }
                  >
                    {t}
                  </button>
                ))}
              </div>
              {fieldError("loadType")}
            </Field>
          </div>
        </div>

        <div className="border-t border-border pt-4">
          <SectionHeader title="Notes & Options" />
          <div className="space-y-3 mt-3">
            <Textarea
              className="text-xs min-h-[72px]"
              placeholder="e.g., Team driver, hazmat certified, flexible on delivery date, prefer drop & hook."
              value={form.notes}
              onChange={(e) => set("notes", e.target.value.slice(0, MAX_NOTES_LENGTH))}
              maxLength={MAX_NOTES_LENGTH}
            />
            <div className="flex flex-wrap gap-1.5">
              {QUICK_NOTES.map((chip) => {
                const isActive = form.notes.toLowerCase().includes(chip.value.toLowerCase());
                return (
                  <button
                    key={chip.value}
                    type="button"
                    className={cn(
                      "px-2 py-0.5 rounded-full text-[10px] font-medium border transition-colors",
                      isActive
                        ? "border-primary bg-primary/10 text-primary"
                        : "border-border bg-muted/50 text-muted-foreground hover:border-primary/50 hover:text-foreground"
                    )}
                    onClick={() => {
                      if (isActive) {
                        let updated = form.notes;
                        updated = updated.replace(new RegExp(`,\\s*${chip.value}`, 'gi'), '');
                        updated = updated.replace(new RegExp(`${chip.value},\\s*`, 'gi'), '');
                        updated = updated.replace(new RegExp(chip.value, 'gi'), '');
                        set("notes", updated.trim().replace(/,\s*$/, '').trim());
                      } else {
                        const current = form.notes.trim();
                        const next = current ? `${current}, ${chip.value}` : chip.value;
                        if (next.length <= MAX_NOTES_LENGTH) set("notes", next);
                      }
                    }}
                  >
                    {chip.label}
                  </button>
                );
              })}
            </div>
            <div className="text-[10px] text-muted-foreground text-right">{form.notes.length}/{MAX_NOTES_LENGTH}</div>
            <div className="flex items-start gap-3">
              <Switch checked={form.visible} onCheckedChange={(v) => set("visible", v)} />
              <div>
                <span className="text-xs font-semibold text-foreground">Make this truck visible to all brokers</span>
                <p className="text-[10px] text-muted-foreground mt-0.5">Turn off to keep this capacity private to your invited network.</p>
              </div>
            </div>
          </div>
        </div>

        <div className="border-t border-border pt-4 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-end gap-2">
          <button
            type="button"
            onClick={handleClear}
            className="text-[11px] font-medium text-muted-foreground hover:text-foreground transition-colors px-2 py-1"
            disabled={submitting}
          >
            Clear form
          </button>
          <Button variant="outline" size="sm" className="text-xs" onClick={handleDraft} disabled={submitting}>
            Save as Draft
          </Button>
          {onSaveTemplate && (
            <Button variant="outline" size="sm" className="text-xs" onClick={onSaveTemplate} disabled={submitting}>
              Save as Template
            </Button>
          )}
          <Button
            size="sm"
            className="h-10 px-6 text-xs font-bold uppercase tracking-wide transition-all duration-150 hover:shadow-lg active:scale-[0.98]"
            style={{
              background: "linear-gradient(135deg, hsl(170 100% 50%), hsl(170 85% 32%))",
              color: "hsl(215 60% 12%)",
              boxShadow: "0 4px 20px hsl(170 100% 50% / 0.3)",
            }}
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? <><Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> {editTruckId ? "Updating…" : "Posting…"}</> : editTruckId ? "Update Truck" : "Post Truck"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionHeader({ title }: { title: string }) {
  return (
    <h5
      className="text-[10px] font-bold uppercase tracking-wider"
      style={{ color: "hsl(var(--brand-teal-deep))" }}
    >
      {title}
    </h5>
  );
}

function Field({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) {
  return (
    <div>
      <Label className="text-[11px] font-medium text-muted-foreground mb-1 block">
        {label}{required && <span style={{ color: "hsl(var(--danger))" }}> *</span>}
      </Label>
      {children}
    </div>
  );
}

export { INITIAL_DATA, EMPTY_DATA };
